
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { User } from '@/types/user';
import { UserRoleType } from '@/types/roles';

export function useUserManagement() {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Create a new user
   */
  const createUser = async (email: string, password: string, role: string = 'viewer') => {
    setIsLoading(true);
    try {
      // Call the Edge Function instead of direct admin API
      const { data, error } = await supabase.functions.invoke('user-management', {
        body: {
          action: 'createUser',
          data: { email, password, role }
        }
      });

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || 'Failed to create user');

      return data.user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a user
   */
  const deleteUser = async (userId: string): Promise<void> => {
    setIsLoading(true);
    try {
      // Call the Edge Function instead of direct admin API
      const { data, error } = await supabase.functions.invoke('user-management', {
        body: {
          action: 'deleteUser',
          data: { userId }
        }
      });

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || 'Failed to delete user');

      toast({
        title: 'Success',
        description: 'User deleted successfully',
      });

    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete user',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle a user's admin status
   */
  const toggleAdminStatus = async (userId: string, makeAdmin: boolean): Promise<void> => {
    setIsLoading(true);
    try {
      const newRole = makeAdmin ? 'admin' : 'member';
      
      // Call the Edge Function instead of direct database API
      const { data, error } = await supabase.functions.invoke('user-management', {
        body: {
          action: 'updateUserRole',
          data: { userId, role: newRole }
        }
      });

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || 'Failed to update user role');

      toast({
        title: 'Success',
        description: `User is now ${makeAdmin ? 'an admin' : 'a member'}`,
      });

    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update user role',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle user active status
   */
  const toggleUserActive = async (userId: string, currentlyActive: boolean): Promise<void> => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('user-management', {
        body: {
          action: 'toggleUserActive',
          data: { userId, isActive: !currentlyActive }
        }
      });

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || 'Failed to update user status');

      toast({
        title: 'Success',
        description: `User is now ${!currentlyActive ? 'active' : 'inactive'}`,
      });

    } catch (error) {
      console.error('Error updating user status:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update user status',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Change user role
   */
  const changeUserRole = async (userId: string, newRole: string): Promise<void> => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('user-management', {
        body: {
          action: 'updateUserRole',
          data: { userId, role: newRole }
        }
      });

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || 'Failed to update user role');

      toast({
        title: 'Success',
        description: `User role changed to ${newRole}`,
      });

    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update user role',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createUser,
    deleteUser,
    toggleAdminStatus,
    toggleUserActive,
    changeUserRole,
    isLoading,
  };
}
